import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import TawkToChat from "../components/TawkToChat";
import {
  fetchCandidates,
  setSelectedTab,
  setSortBy,
  toggleSortDirection,
  setFilterKey,
  setFilterValue,
  setStatusFilter,
  setScoreRange,
  clearAllFilters,
  selectAllCandidates,
  selectCandidatesLoading,
  selectCandidatesError,
  selectSelectedTab,
  selectSortBy,
  selectSortDirection,
  selectFilterKey,
  selectFilterValue,
  selectStatusFilter,
  selectScoreRange,
  selectSelectedCandidates,
  selectTabCounts,
  selectActiveFilterCount,
} from "../utils/redux/candidateSlice";
import { ColorRing } from "react-loader-spinner";
import * as XLSX from "xlsx";
import Navbar from "../components/Navbar";
import JobPostingDescription from "../components/JobPostingDescription";
import CandidateList from "../components/CandidateList";
import CandidateDetailsModal from "../components/CandidateDetailsModal";
import { signOut } from "firebase/auth";
import {
  selectAllUsers,
  selectUserByEmail,
  fetchUserByEmail,
} from "../utils/redux/userDataSlice";
import { useAuth } from "../useAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFileExcel,
  faChevronLeft,
  faChevronRight,
  faArrowUp,
  faArrowDown,
  faFilter,
  faTimes,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const CANDIDATES_PER_PAGE = 10;

const TAB_DEFINITIONS = [
  { key: "all", label: "Tout" },
  { key: "Rejeter", label: "Rejeté" },
  { key: "Evaluation", label: "Évaluation" },
  { key: "Interview", label: "Interview" },
  { key: "Offer", label: "Offre" },
];

const CandidateListPage = () => {
  const { jobID, tenantID } = useParams();
  const { currentUser } = useAuth();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const userData = useSelector((state) =>
    selectUserByEmail(state, currentUser.email)
  );
  const candidates = useSelector(selectAllCandidates);
  const selectedCandidates = useSelector(selectSelectedCandidates);
  const loading = useSelector(selectCandidatesLoading);
  const error = useSelector(selectCandidatesError);
  const selectedTab = useSelector(selectSelectedTab);
  const sortBy = useSelector(selectSortBy);
  const sortDirection = useSelector(selectSortDirection);
  const filterKey = useSelector(selectFilterKey);
  const filterValue = useSelector(selectFilterValue);
  const statusFilter = useSelector(selectStatusFilter);
  const scoreRange = useSelector(selectScoreRange);
  const tabCounts = useSelector(selectTabCounts);
  const activeFilterCount = useSelector(selectActiveFilterCount);

  const [currentPage, setCurrentPage] = useState(1);
  const [reportButtonBusy, setReportButtonBusy] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    document.title = "Candidates List Page - ProRecruit";
    return () => {
      document.title = "ProRecruit";
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(fetchCandidates({ tenantID, jobID }));
      dispatch(fetchUserByEmail(currentUser.email));
    };
    fetchData();
  }, [tenantID, jobID, dispatch, currentUser]);

  // Pagination calculations
  const indexOfLastCandidate = currentPage * CANDIDATES_PER_PAGE;
  const indexOfFirstCandidate = indexOfLastCandidate - CANDIDATES_PER_PAGE;
  const currentCandidates = selectedCandidates.slice(
    indexOfFirstCandidate,
    indexOfLastCandidate
  );
  const totalPages = Math.ceil(
    selectedCandidates.length / CANDIDATES_PER_PAGE
  );

  const handleTabChange = (tab) => {
    dispatch(setSelectedTab({ tab, allCandidates: candidates }));
    setCurrentPage(1);
  };

  const handleSortChange = (sortByValue) => {
    dispatch(setSortBy({ sortKey: sortByValue }));
    setCurrentPage(1);
  };

  const handleFilterKeyChange = (e) => {
    dispatch(setFilterKey(e.target.value));
    setCurrentPage(1);
  };

  const handleFilterValueChange = (value) => {
    dispatch(setFilterValue({ filterKey, filterValue: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    dispatch(clearAllFilters());
    setCurrentPage(1);
    setShowAdvancedFilters(false);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const onReportClick = () => {
    try {
      setReportButtonBusy(true);
      const reportArray = candidates.map((candidate) => ({
        // ... your report data mapping ...
      }));
      const worksheet = XLSX.utils.json_to_sheet(reportArray);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, "DataSheet.xlsx");
      setReportButtonBusy(false);
    } catch (err) {
      setReportButtonBusy(false);
      console.error("Error generating report:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-slate-50">
        <ColorRing
          visible={true}
          height="80"
          width="80"
          ariaLabel="blocks-loading"
          wrapperStyle={{}}
          wrapperClass="blocks-wrapper"
          colors={["#A47713", "#0077B6", "#f8b26a", "#abbd81", "#849b87"]}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-center bg-slate-50">
        <h1 className="text-3xl font-bold text-red-500 mb-4">Erreur</h1>
        <p className="text-lg text-slate-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <Navbar
        onSignOut={() => dispatch(signOut())}
        onCreateJob={() => {}}
        userData={userData}
        extraButton={{ label: "Liste Des Offres", path: "/jobpostings" }}
      />

      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {state?.posting?.Titre ?? "Candidats"}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-slate-500">
                {selectedCandidates.length} candidat{selectedCandidates.length !== 1 ? "s" : ""}
              </span>
              {activeFilterCount > 0 && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#A47713]/10 text-[#A47713]">
                  {activeFilterCount} filtre{activeFilterCount !== 1 ? "s" : ""} actif{activeFilterCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          <button
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#A47713] hover:bg-[#8a6310] text-white font-semibold rounded-xl text-sm transition-colors duration-200 disabled:opacity-50"
            type="button"
            onClick={onReportClick}
            disabled={reportButtonBusy}
          >
            <FontAwesomeIcon
              icon={reportButtonBusy ? faSpinner : faFileExcel}
              className={reportButtonBusy ? "animate-spin" : ""}
            />
            Générer le rapport
          </button>
        </div>

        {/* ── Pill Tab Bar ── */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TAB_DEFINITIONS.map(({ key, label }) => (
            <button
              key={key}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedTab === key
                  ? "bg-[#A47713] text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-[#A47713]/40 hover:text-[#A47713]"
              }`}
              onClick={() => handleTabChange(key)}
            >
              {label}
              <span
                className={`ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                  selectedTab === key
                    ? "bg-white/20 text-white"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {tabCounts[key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* ── Filter & Sort Toolbar ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            {/* Search Input */}
            <div className="relative flex-1 w-full lg:w-auto">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"
              />
              <input
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#A47713] focus:ring-2 focus:ring-[#A47713]/20 transition-all"
                type="text"
                placeholder="Rechercher un candidat..."
                value={filterValue}
                onChange={(e) => handleFilterValueChange(e.target.value)}
              />
            </div>

            {/* Filter by key */}
            <select
              className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-[#A47713] focus:ring-2 focus:ring-[#A47713]/20 bg-white cursor-pointer"
              value={filterKey}
              onChange={handleFilterKeyChange}
            >
              <option value="FullName">Nom</option>
              <option value="ContactInformation.Email">Email</option>
              <option value="Sex">Sexe</option>
              <option value="Skills">Compétences</option>
            </select>

            {/* Sort */}
            <div className="flex items-center gap-1">
              <select
                className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-[#A47713] focus:ring-2 focus:ring-[#A47713]/20 bg-white cursor-pointer"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
              >
                <option value="Score">Score</option>
                <option value="ApplicationDate">Date</option>
                <option value="FullName">Nom</option>
              </select>
              <button
                onClick={() => dispatch(toggleSortDirection())}
                className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#A47713] hover:text-[#A47713] transition-colors"
                title={sortDirection === "desc" ? "Décroissant" : "Croissant"}
              >
                <FontAwesomeIcon
                  icon={sortDirection === "desc" ? faArrowDown : faArrowUp}
                  className="text-sm"
                />
              </button>
            </div>

            {/* Advanced filter toggle */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                showAdvancedFilters || activeFilterCount > 0
                  ? "border-[#A47713] text-[#A47713] bg-[#A47713]/5"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <FontAwesomeIcon icon={faFilter} className="text-xs" />
              Filtres
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-[#A47713] text-white text-xs flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Clear all */}
            {activeFilterCount > 0 && (
              <button
                onClick={handleClearFilters}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
                Effacer
              </button>
            )}
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Status filter */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">
                  Statut
                </label>
                <select
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-[#A47713] focus:ring-2 focus:ring-[#A47713]/20 bg-white"
                  value={statusFilter}
                  onChange={(e) => {
                    dispatch(setStatusFilter(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Tous les statuts</option>
                  <option value="Evaluation">Évaluation</option>
                  <option value="Interview">Interview</option>
                  <option value="Offer">Offre</option>
                  <option value="Rejeter">Rejeté</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>

              {/* Score Min */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">
                  Score minimum
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-[#A47713] focus:ring-2 focus:ring-[#A47713]/20"
                  value={scoreRange.min ?? ""}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    dispatch(setScoreRange({ min: val, max: scoreRange.max }));
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Score Max */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">
                  Score maximum
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="100"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-[#A47713] focus:ring-2 focus:ring-[#A47713]/20"
                  value={scoreRange.max ?? ""}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    dispatch(setScoreRange({ min: scoreRange.min, max: val }));
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Job Posting Description ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
          <JobPostingDescription description={state?.posting?.Description} />
        </div>

        {/* ── Candidate List ── */}
        <CandidateList
          candidates={currentCandidates}
          tenantId={tenantID}
          jobUuid={jobID}
        />

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-8">
            <p className="text-sm text-slate-500">
              {indexOfFirstCandidate + 1}–
              {Math.min(indexOfLastCandidate, selectedCandidates.length)} sur{" "}
              {selectedCandidates.length}
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 rounded-full flex items-center justify-center border border-slate-200 text-slate-500 hover:border-[#A47713] hover:text-[#A47713] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => paginate(i + 1)}
                  className={`w-9 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                    currentPage === i + 1
                      ? "bg-[#A47713] text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 rounded-full flex items-center justify-center border border-slate-200 text-slate-500 hover:border-[#A47713] hover:text-[#A47713] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CandidateDetailsModal />
      <TawkToChat />
    </div>
  );
};

export default CandidateListPage;